import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/events/event-soir.png";

const jazzAJuanEn: ChauffeurPage = {
  slug: "jazz-a-juan",
  title: "Private chauffeur Jazz à Juan – Antibes Juan-les-Pins",
  subtitle:
    "Private chauffeur during Jazz à Juan: access to the Pinède Gould, transfers from Nice airport and late-night returns after the concerts in Juan-les-Pins.",
  keywords: [
    "jazz à juan chauffeur",
    "antibes juan-les-pins jazz festival chauffeur",
    "juan-les-pins private driver",
  ],
  hero: {
    title: "Private chauffeur during Jazz à Juan",
    subtitle:
      "Concerts at the Pinède Gould, facing the sea: a dedicated chauffeur when Juan-les-Pins fills up and parking runs out.",
    keywords: [
      "Jazz à Juan chauffeur",
      "Pinède Gould VTC",
      "Nice airport Juan-les-Pins transfer",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during the Jazz à Juan festival",
    badges: ["Airport welcome", "Pinède Gould drop-off", "Late-night returns"],
  },
  benefits: {
    heading: "Why a chauffeur during Jazz à Juan",
    items: [
      {
        title: "Juan-les-Pins with no parking",
        description:
          "In July, the seaside resort is packed and spaces are scarce near the Pinède Gould: the chauffeur drops you at the entrance and leaves.",
      },
      {
        title: "Nice airport handled",
        description:
          "The airport is around twenty kilometres away. Name-board welcome, flight tracking and a direct transfer to your hotel in Antibes or on the Cap.",
      },
      {
        title: "Concerts by the sea",
        description:
          "The Pinède Gould plays late: the chauffeur waits and secures your return the moment the concert ends, with no taxi hunt.",
      },
      {
        title: "5.0/5 from 38 Google reviews",
        description:
          "Punctuality, discretion and immaculate vehicles: the consistency that earns KS Rent Services a 5.0/5 rating from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during Jazz à Juan",
    services: [
      {
        title: "Nice airport meet-and-greet",
        text: "Nice ↔ Juan-les-Pins / Antibes transfer with flight tracking during the July crowds.",
      },
      {
        title: "Pinède Gould drop-off",
        text: "Approach as close as possible to the festival entrance, within the traffic and concert timing.",
      },
      {
        title: "Evening hire",
        text: "A dedicated chauffeur for a seafront dinner and the concert, waiting on site.",
      },
      {
        title: "Late-night returns",
        text: "A direct return to your hotel in Antibes, Cap d'Antibes, Cannes or Nice after the concert.",
      },
      {
        title: "Group & guest shuttles",
        text: "Multi-vehicle coordination for families, groups and guests.",
      },
    ],
  },
  locations: {
    heading: "Key points during Jazz à Juan",
    items: [
      {
        name: "Pinède Gould",
        description:
          "Legendary open-air stage facing the bay of Juan-les-Pins.",
      },
      {
        name: "Juan-les-Pins",
        description: "Seafront, casino, restaurants and the resort's hotels.",
      },
      {
        name: "Antibes & Cap d'Antibes",
        description: "Old Antibes, Port Vauban and the Cap's residences.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description: "Around twenty kilometres away, Terminals 1 and 2.",
      },
      {
        name: "Antibes train station",
        description: "Rail arrivals, minutes from the Pinède.",
      },
    ],
  },
  generic: {
    heading: "Jazz à Juan from a chauffeur's seat",
    subheading:
      "Every summer in July, Juan-les-Pins hosts one of the oldest jazz festivals in Europe, at the Pinède Gould facing the sea.",
    paragraphs: [
      "Every July, Jazz à Juan sets its stage at the Pinède Gould, in the heart of Juan-les-Pins. The resort is packed, traffic is dense and parking is rare: a chauffeur drops you as close as possible to the entrance and spares you long minutes of circling.",
      "Most visitors arrive via Nice Côte d'Azur airport, around twenty kilometres away. With flight tracking and a name-board welcome, the transfer to your hotel in Antibes, Cap d'Antibes or Juan-les-Pins runs without waiting.",
      "The Pinède concerts finish late into the night. Hourly hire keeps the same chauffeur for a seafront dinner and the concert, and guarantees a direct return from the very last note.",
    ],
    bullets: [
      "Drop-off nearest the Pinède",
      "Nice airport ~20 km",
      "Returns after the concerts",
    ],
  },
  process: {
    heading: "Booking a chauffeur for Jazz à Juan",
    steps: [
      "Tell us your concert nights, your hotel and your timings.",
      "We plan the drop-offs around the Juan-les-Pins traffic and access to the Pinède.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Direct transfer or hourly hire to cover dinner, concert and return.",
    ],
  },
  faq: [
    {
      q: "How do I book a private chauffeur during Jazz à Juan?",
      a: "Book ahead: in July, Juan-les-Pins and Antibes are besieged and demand rises on concert nights. Share your dates, your hotel and your timings so we can secure a dedicated chauffeur.",
    },
    {
      q: "Where can the chauffeur drop me near the Pinède Gould?",
      a: "The Pinède Gould sits in the heart of Juan-les-Pins, in a very busy area in the evening. The chauffeur drops you at the nearest authorised point to the entrance, then collects you at the same spot when the concert ends.",
    },
    {
      q: "Do you handle transfers from Nice airport?",
      a: "Yes, with flight tracking and a name-board welcome. Nice Côte d'Azur airport is around twenty kilometres from Juan-les-Pins: we adjust the pick-up time if your flight is delayed, even in peak season.",
    },
    {
      q: "Is parking difficult during the festival?",
      a: "Yes. In July, the resort is saturated and spaces are rare near the Pinède on concert nights. The chauffeur saves you the search: he drops off, waits or returns at the agreed time.",
    },
    {
      q: "Do you cover late returns after the concerts?",
      a: "Yes. The Pinède concerts finish late and taxis are scarce on the way out: the chauffeur waits and guarantees your direct return to Antibes, Cap d'Antibes, Cannes or Nice.",
    },
    {
      q: "What vehicles do you offer?",
      a: "Saloons, SUVs and vans with a chauffeur depending on your group size, for a comfortable ride between the hotel, dinner and the Pinède Gould.",
    },
    {
      q: "Can you take care of a family or a group?",
      a: "Yes. We coordinate several vehicles for families and groups of friends, with the same level of punctuality for everyone.",
    },
  ],
  relatedLinks: [
    {
      label: "Book a chauffeur in Antibes",
      href: "/en/chauffeur/antibes",
    },
    {
      label: "Private car service in Nice",
      href: "/en/chauffeur/nice",
    },
    {
      label: "Nice Airport transfers",
      href: "/en/chauffeur/airport",
    },
    {
      label: "Private chauffeur on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
  ],
  cta: {
    title: "Your chauffeur for Jazz à Juan",
    text: "Airport welcome, Pinède Gould drop-off and late-night returns: book a dedicated chauffeur for your concert evenings.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Jazz à Juan Chauffeur – Antibes car service | KS Rent",
    description:
      "Private chauffeur during Jazz à Juan: Nice airport transfer, drop-off at the Pinède Gould in Juan-les-Pins and late-night returns after the concerts. Book now.",
    canonical: "/en/chauffeur/jazz-a-juan",
  },
};

export default jazzAJuanEn;
